import type { ActivitySummary } from '@whn/shared';
import { CalendarCheck, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useMyDay, useUpdateActivity } from '@/features/crm/use-crm';
import { useAuth } from '@/features/auth/auth-context';
import { cn, formatDateTime } from '@/lib/utils';

/**
 * "My Day": what is late, what is due today, what is coming. The three states
 * are shown together rather than behind tabs, because the whole point is to
 * see at a glance whether anything is slipping.
 */
export function MyDayPanel() {
  const { can } = useAuth();
  const query = useMyDay();
  const updateActivity = useUpdateActivity();

  if (query.isPending) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>My day</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (query.isError || !query.data) return null;

  const { overdue, dueToday, upcoming, completedToday } = query.data;
  const nothingPending = overdue.length + dueToday.length + upcoming.length === 0;

  const complete = (activity: ActivitySummary) => {
    updateActivity.mutate({ id: activity.id, input: { status: 'COMPLETED' } });
  };

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1">
          <CardTitle>My day</CardTitle>
          <CardDescription>
            {completedToday > 0
              ? `${completedToday} completed today`
              : 'Everything assigned to you, soonest first'}
          </CardDescription>
        </div>
        <Link to="/activities" className="text-sm font-medium text-brand hover:underline">
          All activities
        </Link>
      </CardHeader>

      <CardContent className="space-y-4">
        {nothingPending ? (
          <div className="flex items-center gap-3 rounded-md bg-positive-soft px-4 py-3">
            <CheckCircle2 className="size-5 shrink-0 text-positive" aria-hidden="true" />
            <p className="text-sm text-ink">
              Nothing due. {completedToday > 0 ? 'Good work today.' : 'Enjoy the quiet.'}
            </p>
          </div>
        ) : (
          <>
            <Group
              title="Overdue"
              tone="danger"
              activities={overdue}
              onComplete={complete}
              canComplete={can('activity:update')}
            />
            <Group
              title="Due today"
              tone="warning"
              activities={dueToday}
              onComplete={complete}
              canComplete={can('activity:update')}
            />
            <Group
              title="Coming up"
              tone="neutral"
              activities={upcoming}
              onComplete={complete}
              canComplete={can('activity:update')}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Group({
  title,
  tone,
  activities,
  onComplete,
  canComplete,
}: {
  title: string;
  tone: 'danger' | 'warning' | 'neutral';
  activities: ActivitySummary[];
  onComplete: (activity: ActivitySummary) => void;
  canComplete: boolean;
}) {
  if (activities.length === 0) return null;

  return (
    <section className="space-y-1.5">
      <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
        {title}
        <Badge tone={tone}>{activities.length}</Badge>
      </h3>
      <ul className="space-y-1">
        {activities.slice(0, 5).map((activity) => (
          <li
            key={activity.id}
            className="flex items-center gap-3 rounded-md border border-border-subtle px-3 py-2"
          >
            <input
              type="checkbox"
              className="size-4 shrink-0 accent-[var(--color-brand)]"
              onChange={() => onComplete(activity)}
              disabled={!canComplete}
              aria-label={`Mark "${activity.subject}" complete`}
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-ink">{activity.subject}</p>
              <p className="truncate text-xs text-ink-subtle">
                {activity.relatedTo ? `${activity.relatedTo.record.name} · ` : null}
                <span className={cn(activity.isOverdue && 'text-danger')}>
                  {formatDateTime(activity.dueAt ?? activity.startAt)}
                </span>
              </p>
            </div>
            <CalendarCheck className="size-4 shrink-0 text-ink-subtle" aria-hidden="true" />
          </li>
        ))}
      </ul>
    </section>
  );
}
